import { useQuery } from '@tanstack/react-query';
import {
  PropsWithChildren,
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { LanguageCode } from '@/ifaces/language';
import { UserType } from '@/ifaces/user';
import { Customer } from '../../ifaces/customers';
import { DEFAULT_ENTITLEMENTS, Entitlements } from '../../ifaces/entitlements';
import { OldProfile } from '../../ifaces/profile';
import { posthog } from '../components/Posthog';
import {
  triggerLanguageCodeChangeEvent,
  triggerSetEntitlementsEvent,
  triggerShowPromoFooterEvent,
} from '../util/custom-events';
import { loadCatalog } from '../util/i18n';
import { fetchJson } from '../util/xhr';

// @ts-expect-error import.meta.env is available in Vite
const PROMO_LINK = import.meta.env.VITE_PROMO_LINK;

type AuthedUserContextType = { ready: boolean; error: boolean } & AuthedUser;

export type AuthedUser = {
  user_id: string;
  email: string;
  entitlements: Entitlements;
  profile: OldProfile;
  onboarding: boolean;
  aiPolicy: boolean;
  user_type: UserType;
  customer_id: string;
  customer_name: string;
  customer: Customer;
};

const AuthedUserContext = createContext<AuthedUserContextType>({
  ready: false,
  error: false,
  user_id: '',
  email: '',
  profile: {
    full_name: '',
    company: '',
    role: '',
    language_code: LanguageCode.English,
  },
  onboarding: false,
  aiPolicy: false,
  entitlements: DEFAULT_ENTITLEMENTS,
  user_type: UserType.Anonymous,
  customer_id: '',
  customer_name: '',
  customer: {
    id: '',
    name: '',
    hideBeta: false,
    enableAiIntroduction: false,
    requireAiPolicy: false,
    showProgressTracker: false,
    enablePracticeMode: false,
    enableAIAcademyLink: false,
  },
});

export function useAuthedUserContext() {
  return useContext(AuthedUserContext);
}

export const AuthedUserContextProvider = (props: PropsWithChildren) => {
  const query = useQuery<AuthedUser>({
    queryKey: ['authed-user'],
    queryFn: () => fetchJson('api/auth'),
  });

  const ready = query.isFetched && !query.isError && !!query.data;
  const language_code = query.data?.profile?.language_code || LanguageCode.English;
  useEffect(() => {
    if (ready) {
      loadCatalog(language_code);
      triggerLanguageCodeChangeEvent(language_code as LanguageCode);
    }
  }, [ready, language_code]);

  const entitlements = query.data?.entitlements || DEFAULT_ENTITLEMENTS;
  useEffect(() => {
    if (ready) {
      triggerSetEntitlementsEvent(entitlements);
    }
  }, [ready, entitlements]);

  useEffect(() => {
    if (query.isError) {
      console.error('Failed to fetch authed user', query.error);
    }
  }, [query.isError, query.error]);

  const value: AuthedUserContextType = useMemo(
    () => ({
      ready,
      error: query.isError,
      user_id: query.data?.user_id || '',
      email: query.data?.email || '',
      profile: {
        full_name: query.data?.profile?.full_name || '',
        company: query.data?.profile?.company || '',
        role: query.data?.profile?.role || '',
        language_code: query.data?.profile?.language_code || '',
      },
      onboarding: !!query.data?.onboarding,
      aiPolicy: !!query.data?.aiPolicy,
      entitlements: {
        ...DEFAULT_ENTITLEMENTS,
        ...(query.data?.entitlements || {}),
      },
      user_type: query.data?.user_type || UserType.Anonymous,
      customer_id: query.data?.customer_id || '',
      customer_name: query.data?.customer_name || '',
      customer: {
        id: query.data?.customer?.id || '',
        name: query.data?.customer?.name || '',
        hideBeta: query.data?.customer?.hideBeta || false,
        enableAiIntroduction: query.data?.customer?.enableAiIntroduction || false,
        requireAiPolicy: query.data?.customer?.requireAiPolicy || false,
        showProgressTracker: query.data?.customer?.showProgressTracker || false,
        enablePracticeMode: query.data?.customer?.enablePracticeMode || false,
        enableAIAcademyLink: query.data?.customer?.enableAIAcademyLink || false,
      },
    }),
    [query.data, query.isError, ready],
  );

  const isCustomer = useMemo(() => value.user_type === UserType.Consumer, [value.user_type]);
  useEffect(() => {
    if (isCustomer && PROMO_LINK) {
      triggerShowPromoFooterEvent(`${PROMO_LINK}`);
    }
  }, [value, isCustomer]);

  useEffect(() => {
    if (value.email) {
      posthog.identify(value.email);
    }
  }, [value.email]);

  return createElement(AuthedUserContext.Provider, { value }, props.children);
};
